import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { useStores } from 'src/components/common/root-store-provider/root-store-provider';

import { useAnalytics } from '@features/Analytics';

type TpQueryValue = string | string[] | undefined;

type TpUnsubscribeAnalyticsParams = {
  email?: TpQueryValue;
  userId?: TpQueryValue;
};

export type TpUnsubscribeEventOptions = {
  email: string;
  userId: string;
  [key: string]: unknown;
};

type TpUnsubscribeRequestOptions = Record<string, unknown>;

const getQueryStringValue = (value: TpQueryValue): string => {
  if (Array.isArray(value)) {
    return value[0] ?? '';
  }

  return value ?? '';
};

export const getUnsubscribeEventOptions = (
  params: TpUnsubscribeAnalyticsParams = {},
  options: Record<string, unknown> = {},
): TpUnsubscribeEventOptions => ({
  email: getQueryStringValue(params.email),
  userId: getQueryStringValue(params.userId),
  ...options,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (typeof value === 'number') {
    return `${value}`;
  }

  return undefined;
};

const getNumberValue = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

export const getUnsubscribeRequestSuccessOptions = (
  options: TpUnsubscribeRequestOptions = {},
  statusCode?: number,
): Record<string, unknown> => ({
  ...options,
  status: 'success',
  ...(statusCode != null ? { statusCode } : {}),
});

export const getUnsubscribeRequestErrorOptions = (
  error: unknown,
  options: TpUnsubscribeRequestOptions = {},
): Record<string, unknown> => {
  const errorRecord = isRecord(error) ? error : {};
  const response = isRecord(errorRecord.response) ? errorRecord.response : {};
  const responseData = isRecord(response.data) ? response.data : {};
  const cause = error instanceof Error ? error.cause : undefined;
  const causeResponse = cause instanceof Response ? cause : undefined;

  const statusCode =
    getNumberValue(response.status) ?? getNumberValue(causeResponse?.status);
  const errorCode =
    getStringValue(responseData.code) ??
    getStringValue(responseData.error_code) ??
    getStringValue(responseData.error) ??
    getStringValue(errorRecord.code) ??
    (statusCode != null ? `${statusCode}` : 'unknown');
  const errorMessage =
    getStringValue(responseData.message) ??
    getStringValue(errorRecord.message) ??
    getStringValue(causeResponse?.statusText);

  return {
    ...options,
    status: 'error',
    errorCode,
    ...(statusCode != null ? { statusCode } : {}),
    ...(errorMessage != null ? { errorMessage } : {}),
  };
};

export const useUnsubscribeAnalytics = (
  params: TpUnsubscribeAnalyticsParams = {},
) => {
  const { query } = useRouter();
  const { authStore } = useStores();
  const { trackGoogleEvent } = useAnalytics();

  const email =
    getQueryStringValue(params.email) ||
    getQueryStringValue(query.email) ||
    authStore?.user?.email ||
    '';
  const userId =
    getQueryStringValue(params.userId) ||
    getQueryStringValue(query.user_id) ||
    (authStore?.user?.id ? `${authStore.user.id}` : '');

  const defaultEventOptions = useMemo(
    () => getUnsubscribeEventOptions({ email, userId }),
    [email, userId],
  );

  const trackUnsubscribeEvent = useCallback(
    (eventName: string, options: Record<string, unknown> = {}) => {
      trackGoogleEvent({
        eventName,
        options: getUnsubscribeEventOptions({ email, userId }, options),
      });
    },
    [email, trackGoogleEvent, userId],
  );

  const trackUnsubscribeRequestSuccess = useCallback(
    (
      eventName: string,
      options: TpUnsubscribeRequestOptions = {},
      statusCode?: number,
    ) => {
      trackUnsubscribeEvent(
        eventName,
        getUnsubscribeRequestSuccessOptions(options, statusCode),
      );
    },
    [trackUnsubscribeEvent],
  );

  const trackUnsubscribeRequestError = useCallback(
    (
      eventName: string,
      error: unknown,
      options: TpUnsubscribeRequestOptions = {},
    ) => {
      trackUnsubscribeEvent(
        eventName,
        getUnsubscribeRequestErrorOptions(error, options),
      );
    },
    [trackUnsubscribeEvent],
  );

  return {
    defaultEventOptions,
    trackUnsubscribeRequestError,
    trackUnsubscribeRequestSuccess,
    trackUnsubscribeEvent,
  };
};
